import { z } from 'zod';
import {
  ummTypeValues,
  unavailabilityTypeValues,
  ummSortByValues,
  ummStatusValues,
} from 'src/apis/iip-service/mappings';
import type { UmmsSortOrder } from 'src/apis/iip-service/types';
import {
  formOptionSchema,
  getPaginationSchema,
} from 'src/components/form/zod-schemas';
import {
  optionalDefault,
  preprocessAsArray,
} from 'src/components/form/zod-utilities';
import { usePageParamsFromSchema } from 'src/hooks/use-page-params-from-schema';
import type { UnionToTuple } from 'src/utils/labels-to-values';

const schema = z
  .object({
    sortBy: z.enum(ummSortByValues).default('PUBLICATION_DATE_TIME'),
    sortOrder: z
      .enum(['ASC', 'DESC'] satisfies UnionToTuple<UmmsSortOrder>)
      .default('DESC'), // currently not configurable by the user
    marketParticipantIds: z.preprocess(
      preprocessAsArray,
      z.array(formOptionSchema).default([])
    ),
    ummTypes: z.preprocess(
      preprocessAsArray,
      z.array(z.enum(ummTypeValues)).default([])
    ),
    publicationDateTimeFrom: z.string().nullable().default(null),
    publicationDateTimeTo: z.string().nullable().default(null),
    eventPeriodFrom: z.string().nullable().default(null),
    eventPeriodTo: z.string().nullable().default(null),
    messageId: z.string().nullable().default(null),
    assetEic: z.string().nullable().default(null),
    statusList: z.preprocess(
      optionalDefault(['ACTIVE']),
      z.preprocess(preprocessAsArray, z.array(z.enum(ummStatusValues)))
    ),
    unavailabilityTypes: z.preprocess(
      preprocessAsArray,
      z.array(z.enum(unavailabilityTypeValues)).default([])
    ),
  })
  .merge(getPaginationSchema());

export function useUmmsPageParams() {
  return usePageParamsFromSchema(schema, {
    filters: {
      sortBy: { label: 'Sort Messages by' },
      marketParticipantIds: { label: 'Market Participants' },
      ummTypes: { label: 'Information Type' },
      publicationDateTimeFrom: { label: 'Publication Date' },
      publicationDateTimeTo: { label: 'Publication Date' },
      eventPeriodFrom: { label: 'Event Period' },
      eventPeriodTo: { label: 'Event Period' },
      messageId: { label: 'Message ID' },
      statusList: { label: 'Status' },
      assetEic: { label: 'Affected Asset EIC' },
      unavailabilityTypes: { label: 'Unavailability Types' },
    },
  });
}

export type UmmOverviewPageParams = ReturnType<typeof useUmmsPageParams>;
